/**
 * Azamphone global shell: small progressive enhancements for the header.
 * Navigation, search and language switching all work without this file.
 *
 * Lifecycle: everything lives in custom elements, so the Theme Editor's
 * section re-render (shopify:section:load / unload) is handled by
 * connectedCallback / disconnectedCallback on the fresh or removed DOM.
 * Every listener added outside the element is removed on disconnect, and
 * definitions are guarded so the file can safely run more than once.
 */
(() => {
  /* Keep in sync with the 990px breakpoint in base.css / az-shell.css. */
  const DESKTOP_QUERY = '(min-width: 990px)';

  /**
   * <az-mobile-menu>: a trigger button plus a modal <dialog>.
   * - showModal() makes the rest of the page inert, so focus cannot move into
   *   the obscured page; focus starts on the close button.
   * - Escape closes it (native dialog "cancel"), as does the close button.
   * - On close: aria-expanded is reset, expanded groups collapse and focus
   *   returns to the trigger.
   * - Scroll lock comes from critical.css (html:has(dialog[scroll-lock][open]))
   *   and is released whenever the dialog closes or is removed from the DOM.
   * - Crossing into the desktop layout closes it; disconnecting (section
   *   unload / re-render) closes it too.
   */
  class AzMobileMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-menu-open]');
      if (!this.dialog || !this.trigger) return;

      this.media = window.matchMedia(DESKTOP_QUERY);
      this.handleOpen = () => this.open();
      this.handleClick = (event) => {
        if (event.target.closest('[data-menu-close]')) this.close();
      };
      this.handleClose = () => this.afterClose();
      this.handleMedia = (event) => {
        if (event.matches) this.close();
      };

      this.trigger.addEventListener('click', this.handleOpen);
      this.dialog.addEventListener('click', this.handleClick);
      this.dialog.addEventListener('close', this.handleClose);
      this.media.addEventListener('change', this.handleMedia);
    }

    disconnectedCallback() {
      if (!this.dialog || !this.trigger) return;
      this.trigger.removeEventListener('click', this.handleOpen);
      this.dialog.removeEventListener('click', this.handleClick);
      this.dialog.removeEventListener('close', this.handleClose);
      this.media.removeEventListener('change', this.handleMedia);
      this.close();
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.isOpen = true;
      this.trigger.setAttribute('aria-expanded', 'true');
      const closeButton = this.dialog.querySelector('[data-menu-close]');
      if (closeButton) closeButton.focus();
    }

    /* Clean up synchronously for our own close paths; the native "close"
     * event (fired asynchronously, e.g. after Escape) covers the rest.
     * isOpen makes the cleanup run once per opening. */
    close() {
      if (this.dialog.open) this.dialog.close();
      this.afterClose();
    }

    afterClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.dialog.querySelectorAll('details[open]').forEach((details) => {
        details.open = false;
      });
      /* Only refocus a trigger that is still rendered (not after a switch to desktop). */
      if (this.isConnected && this.trigger.getClientRects().length > 0) {
        this.trigger.focus();
      }
    }
  }

  /**
   * <az-disclosure-group>: coordinates the <details> it contains (desktop
   * category dropdowns, mobile search toggle). Not modal, no focus trap.
   * - Opening one closes the others.
   * - Escape closes the open one and returns focus to its <summary>.
   * - A pointer press outside the group closes it; keyboard focus leaving an
   *   open <details> (to anywhere else) closes that one.
   * - On open, an element marked [data-autofocus] inside it receives focus.
   */
  class AzDisclosureGroup extends HTMLElement {
    connectedCallback() {
      this.handleToggle = (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !this.owns(details)) return;
        if (details.open) {
          this.groups().forEach((other) => {
            if (other !== details) other.open = false;
          });
          const autofocus = details.querySelector('[data-autofocus]');
          if (autofocus) autofocus.focus();
        }
      };
      this.handleKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.groups().find((details) => details.open);
        if (!open) return;
        open.open = false;
        const summary = open.querySelector('summary');
        if (summary) summary.focus();
      };
      this.handlePointer = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this.handleFocusOut = (event) => {
        const next = event.relatedTarget;
        if (!next) return;
        this.groups().forEach((details) => {
          if (details.open && !details.contains(next)) details.open = false;
        });
      };

      /* "toggle" does not bubble, so listen in the capture phase. */
      this.addEventListener('toggle', this.handleToggle, true);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('pointerdown', this.handlePointer);
    }

    disconnectedCallback() {
      this.removeEventListener('toggle', this.handleToggle, true);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('pointerdown', this.handlePointer);
    }

    /* Only the group's own top-level <details>, not nested ones. */
    owns(details) {
      return details.parentElement && details.parentElement.closest('az-disclosure-group') === this;
    }

    groups() {
      return Array.from(this.querySelectorAll('details')).filter((details) => this.owns(details));
    }

    closeAll() {
      this.groups().forEach((details) => {
        details.open = false;
      });
    }
  }

  /**
   * <az-sheet>: the mobile collection filter sheet. Same modal behaviour as
   * <az-mobile-menu> (trigger [data-menu-open], close [data-menu-close],
   * Escape, focus return, scroll lock, closes at the desktop breakpoint).
   */
  class AzSheet extends AzMobileMenu {}

  /**
   * <az-catalog-menu>: the desktop catalog dropdown (brands on the left,
   * the hovered or focused brand's models on the right). Brand links still
   * open the brand's collection when clicked. Without JavaScript every
   * model panel is shown, stacked (see az-shell.css).
   */
  class AzCatalogMenu extends HTMLElement {
    connectedCallback() {
      this.brands = Array.from(this.querySelectorAll('[data-catalog-brand]'));
      this.panels = Array.from(this.querySelectorAll('[data-catalog-panel]'));
      if (!this.brands.length) return;
      this.handleActivate = (event) => {
        const brand = event.target.closest('[data-catalog-brand]');
        if (brand && this.contains(brand)) this.activate(brand.dataset.catalogBrand);
      };
      this.addEventListener('pointerover', this.handleActivate);
      this.addEventListener('focusin', this.handleActivate);
      const current = this.brands.find((brand) => brand.hasAttribute('aria-current')) || this.brands[0];
      this.activate(current.dataset.catalogBrand);
    }

    disconnectedCallback() {
      if (!this.handleActivate) return;
      this.removeEventListener('pointerover', this.handleActivate);
      this.removeEventListener('focusin', this.handleActivate);
    }

    activate(id) {
      this.brands.forEach((brand) => brand.classList.toggle('is-active', brand.dataset.catalogBrand === id));
      this.panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.catalogPanel === id));
    }
  }

  /**
   * <az-predictive-search>: product suggestions under the search field,
   * fetched from Shopify's predictive search through the Section Rendering
   * API (sections/az-predictive-search.liquid renders them with the theme's
   * own card styles and money format). The plain GET search keeps working
   * without it. Arrow keys move through the suggestions, Enter opens the
   * highlighted one, Escape closes the list.
   */
  class AzPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-suggest-panel]');
      this.url = this.dataset.url;
      if (!this.input || !this.panel || !this.url || typeof window.fetch !== 'function') return;

      this.handleInput = () => {
        window.clearTimeout(this.timer);
        const terms = this.input.value.trim();
        if (terms.length < 2) {
          this.close();
          return;
        }
        this.timer = window.setTimeout(() => this.search(terms), 250);
      };
      this.handleFocus = () => {
        if (this.panel.childElementCount > 0 && this.input.value.trim().length >= 2) this.open();
      };
      this.handleKeydown = (event) => this.onKeydown(event);
      this.handlePointer = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('focus', this.handleFocus);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('pointerdown', this.handlePointer);
      this.input.setAttribute('aria-expanded', 'false');
    }

    disconnectedCallback() {
      if (!this.handleInput) return;
      window.clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('focus', this.handleFocus);
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('pointerdown', this.handlePointer);
    }

    async search(terms) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = new URL(this.url, window.location.origin);
      url.searchParams.set('q', terms);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('section_id', 'az-predictive-search');
      try {
        const response = await fetch(url.href, {
          signal: this.controller.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const results = doc.querySelector('[data-suggest-results]');
        /* Ignore a late answer for terms the visitor has already changed. */
        if (!results || this.input.value.trim() !== terms) return;
        this.panel.innerHTML = results.innerHTML;
        this.open();
      } catch (error) {
        if (!error || error.name !== 'AbortError') this.close();
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.highlight(-1);
    }

    options() {
      return Array.from(this.panel.querySelectorAll('[data-suggest-option]'));
    }

    highlight(index) {
      this.options().forEach((option, i) => {
        const active = i === index;
        option.classList.toggle('is-active', active);
        if (active) {
          if (!option.id) option.id = `${this.input.id}-option-${i}`;
          this.input.setAttribute('aria-activedescendant', option.id);
        }
      });
      if (index < 0) this.input.removeAttribute('aria-activedescendant');
    }

    onKeydown(event) {
      if (this.panel.hidden) return;
      const options = this.options();
      const current = options.findIndex((option) => option.classList.contains('is-active'));
      if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        this.highlight((current + 1) % options.length);
      } else if (event.key === 'ArrowUp' && options.length) {
        event.preventDefault();
        this.highlight((current - 1 + options.length) % options.length);
      } else if (event.key === 'Enter' && current > -1) {
        event.preventDefault();
        window.location.assign(options[current].href);
      } else if (event.key === 'Escape') {
        /* Close only the suggestions; the mobile search bar stays open. */
        event.stopPropagation();
        this.close();
        this.input.focus();
      }
    }
  }

  /* Drop empty filter parameters (e.g. blank price inputs) from submitted
   * filter forms, so URLs stay clean. Registered once per page. */
  if (!window.__azFilterFormData) {
    window.__azFilterFormData = true;
    document.addEventListener('formdata', (event) => {
      if (!event.target.classList || !event.target.classList.contains('az-filters')) return;
      Array.from(event.formData.entries()).forEach(([name, value]) => {
        if (name.startsWith('filter.') && value === '') event.formData.delete(name);
      });
    });
  }

  if (!customElements.get('az-mobile-menu')) customElements.define('az-mobile-menu', AzMobileMenu);
  if (!customElements.get('az-disclosure-group')) customElements.define('az-disclosure-group', AzDisclosureGroup);
  if (!customElements.get('az-sheet')) customElements.define('az-sheet', AzSheet);
  if (!customElements.get('az-catalog-menu')) customElements.define('az-catalog-menu', AzCatalogMenu);
  if (!customElements.get('az-predictive-search')) customElements.define('az-predictive-search', AzPredictiveSearch);
})();
